import { PCAgentClient, createPCAgentClient } from './pcAgent';
import { databaseService } from './database';
import { PCInstance, MultiplayerSession, MultiplayerSessionStatus, MultiplayerSessionDbStatus } from '../types/database';

export interface MultiplayerLaunchParams {
  experienceId: string;
  experienceName: string;
  roomId: string;
  version?: string;
  serverPc: PCInstance;
  clientPcs: PCInstance[];
  operatorId?: string;
  language?: string;
}

type StatusCallback = (session: MultiplayerSession) => void;

// Map UI status to DB status (waiting_for_server is UI-only)
function toDbStatus(status: MultiplayerSessionStatus): MultiplayerSessionDbStatus | null {
  if (status === 'waiting_for_server') return null; // No DB update for this
  return status as MultiplayerSessionDbStatus;
}

/**
 * Orchestrates multiplayer session lifecycle:
 * 1. Launch dedicated server build on the server PC
 * 2. Poll until server is ready (Unreal responds on :8081)
 * 3. Launch client builds on all selected PCs with server IP:7777
 * 4. Monitor session and handle cleanup on failure/abort
 *
 * Persists session state to experiences.multiplayer_sessions in Supabase.
 */
export class MultiplayerOrchestrator {
  private session: MultiplayerSession;
  private onStatusChange: StatusCallback;
  private aborted = false;
  private agentClients: Map<string, PCAgentClient> = new Map();

  constructor(onStatusChange: StatusCallback) {
    this.onStatusChange = onStatusChange;
    this.session = {
      experienceId: '',
      experienceName: '',
      serverPcId: '',
      serverIp: '',
      clientPcIds: [],
      status: 'starting_server',
      startedAt: Date.now(),
    };
  }

  private getClient(pc: PCInstance): PCAgentClient {
    const ip = pc.ip_address || pc.tailscale_ip;
    if (!ip) throw new Error(`No IP address for PC ${pc.pc_name || pc.id}`);

    if (!this.agentClients.has(pc.id)) {
      // LAN first, Tailscale fallback.
      this.agentClients.set(pc.id, createPCAgentClient([pc.ip_address, pc.tailscale_ip]));
    }
    return this.agentClients.get(pc.id)!;
  }

  private updateStatus(status: MultiplayerSessionStatus, error?: string) {
    this.session.status = status;
    if (error) this.session.error = error;
    if (status === 'waiting_for_server') this.session.serverReadyAt = undefined;
    this.onStatusChange({ ...this.session });

    // Fire-and-forget DB update (don't block orchestration)
    const dbStatus = toDbStatus(status);
    if (dbStatus && this.session.id) {
      databaseService.updateMultiplayerSessionStatus(this.session.id, dbStatus, error).catch(() => {});
    }
  }

  getSession(): MultiplayerSession {
    return { ...this.session };
  }

  async startSession(params: MultiplayerLaunchParams): Promise<MultiplayerSession> {
    const serverIp = params.serverPc.ip_address || params.serverPc.tailscale_ip;
    if (!serverIp) {
      this.updateStatus('failed', 'Server PC has no IP address');
      return this.session;
    }

    this.session = {
      experienceId: params.experienceId,
      experienceName: params.experienceName,
      roomId: params.roomId,
      serverPcId: params.serverPc.id,
      serverIp,
      clientPcIds: params.clientPcs.map(pc => pc.id),
      status: 'starting_server',
      startedAt: Date.now(),
    };

    // Persist session to DB
    const sessionId = await databaseService.createMultiplayerSession({
      experienceId: params.experienceId,
      roomId: params.roomId,
      operatorId: params.operatorId,
      serverPcInstanceId: params.serverPc.id,
      serverIp,
      clientPcInstanceIds: params.clientPcs.map(pc => pc.id),
    });

    if (sessionId) {
      this.session.id = sessionId;
    } else {
      console.warn('Failed to create multiplayer session in DB — continuing without persistence');
    }

    try {
      // Phase 1: Launch server build on dedicated server PC via server manager
      this.updateStatus('starting_server');
      const serverClient = this.getClient(params.serverPc);

      const serverResult = await serverClient.launchServer({
        experience_id: params.experienceId,
        version: params.version,
        operator_id: params.operatorId,
        session_id: this.session.id,
        language: params.language,
      });

      if (!serverResult.success) {
        this.updateStatus('failed', `Server launch failed: ${serverResult.error}`);
        return this.session;
      }

      // Store the server instance ID for later status checks and cleanup
      if (serverResult.server_id) {
        this.session.serverId = serverResult.server_id;
      }

      if (this.aborted) return this.session;

      // Phase 2: Wait for server ready (poll dedicated server endpoint)
      this.updateStatus('waiting_for_server');

      const serverReady = this.session.serverId
        ? await serverClient.waitForDedicatedServerReady(this.session.serverId, 60000)
        : false;
      if (!serverReady) {
        this.updateStatus('failed', 'Server startup timed out (60s)');
        await this.killServer(params.serverPc);
        return this.session;
      }

      if (this.aborted) return this.session;

      this.session.serverReadyAt = Date.now();
      // Update DB with server_ready status
      if (this.session.id) {
        databaseService.updateMultiplayerSessionStatus(this.session.id, 'server_ready').catch(() => {});
      }

      // Phase 3: Launch all client builds in parallel
      this.updateStatus('launching_clients');

      const clientResults = await Promise.allSettled(
        params.clientPcs.map(async (pc) => {
          const client = this.getClient(pc);
          return client.launchExperience({
            experience_id: params.experienceId,
            version: params.version,
            operator_id: params.operatorId,
            language: params.language,
            args: { _travel: `${serverIp}:7777` },
          });
        })
      );

      // Check for failures — any failure means the whole session fails
      const failures = clientResults.filter(
        (r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
      );

      if (failures.length > 0) {
        const failedCount = failures.length;
        const totalCount = clientResults.length;
        this.updateStatus('failed', `${failedCount}/${totalCount} clients failed to launch`);
        await this.cleanup(params.serverPc, params.clientPcs, clientResults);
        return this.session;
      }

      // All clients launched successfully
      this.updateStatus('active');
      return this.session;
    } catch (err: any) {
      this.updateStatus('failed', err.message || 'Unknown error');
      await this.cleanup(params.serverPc, params.clientPcs);
      return this.session;
    }
  }

  async stopSession(serverPc: PCInstance, clientPcs: PCInstance[]): Promise<void> {
    this.aborted = true;
    this.updateStatus('stopping');

    // Kill all clients first (parallel)
    await Promise.allSettled(
      clientPcs.map(async (pc) => {
        try {
          const client = this.getClient(pc);
          await client.killExperience(true);
        } catch {
          // Best effort
        }
      })
    );

    // Then kill server
    await this.killServer(serverPc);

    this.updateStatus('completed');
  }

  private async killServer(serverPc: PCInstance): Promise<void> {
    try {
      const client = this.getClient(serverPc);
      if (this.session.serverId) {
        await client.killServer(this.session.serverId);
      } else {
        // Fallback: kill via experience API if no server ID available
        await client.killExperience(true);
      }
    } catch {
      // Best effort
    }
  }

  private async cleanup(
    serverPc: PCInstance,
    clientPcs: PCInstance[],
    clientResults?: PromiseSettledResult<any>[]
  ): Promise<void> {
    // Kill clients that launched successfully
    await Promise.allSettled(
      clientPcs.map(async (pc, index) => {
        // If we have results, only kill clients that launched successfully
        if (clientResults) {
          const result = clientResults[index];
          if (result.status === 'rejected' || !result.value.success) {
            return; // This client never launched, skip
          }
        }
        try {
          const client = this.getClient(pc);
          await client.killExperience(true);
        } catch {
          // Best effort
        }
      })
    );

    // Kill server
    await this.killServer(serverPc);
  }
}
